// What the Budget Simulator preview card and its details page both read off a simulation: its
// finished points for the selected KPI, the historical points and optimal zones of the Total
// Saturation Curve, and the units. The component provides `simulation`, `points`, `client`,
// `report` and a `currentKpi` data field.

import numbro from 'numbro'
import moment from 'moment'
import { aggregatePoints, historicalPoints, totalRegions, outputNames, parsedResults, planPeriods, modelDates } from '@/utils/budgetSimulator.js'

export const budgetSimulation = {
  computed: {
    referenceModelConfig () {
      if (this.client.plan_reference_model) return this.client.plan_reference_model.config
      return this.client.reference_model ? this.client.reference_model.config : {}
    },
    gridSize () {
      return (this.simulation.grid || []).length
    },
    donePoints () {
      return this.points.filter(point => !!parsedResults(point)).length
    },
    isRunning () {
      return this.donePoints < this.gridSize
    },
    kpiNames () {
      const finished = this.points.map(point => parsedResults(point)).find(results => results && results.sales)
      return outputNames(finished, this.client, this.referenceModelConfig)
    },
    rows () {
      return aggregatePoints(this.points, this.currentKpi, this.client)
    },
    periods () {
      const dates = modelDates(this.client, this.referenceModelConfig)
      return planPeriods(this.simulation.date_from, this.simulation.date_to, this.simulation.media_plan_frequency, dates.all, dates.end)
    },
    historical () {
      return historicalPoints(this.report, this.currentKpi, this.simulation.media_plan_frequency, this.periods)
    },
    regions () {
      return totalRegions(this.rows)
    },
    spendUnitName () {
      return this.referenceModelConfig.invest_unit_name || 'USD'
    },
    outputUnitName () {
      const outputs = this.referenceModelConfig.outputs
      if (this.currentKpi && this.currentKpi !== 'Total' && outputs && outputs[this.currentKpi]) {
        return outputs[this.currentKpi].sales_unit_name || 'USD'
      }
      return this.referenceModelConfig.sales_unit_name || 'USD'
    },
    channelLabels () {
      const pretty = (this.client.pretty_names && this.client.pretty_names.media) || {}
      const labels = {}
      Object.keys(this.client.channelColors || {}).concat(Object.keys(this.simulation.mix || {})).forEach(channel => {
        labels[channel] = pretty[channel] || channel
      })
      return labels
    }
  },
  methods: {
    formatDate (date) {
      return moment(date).format('ll')
    },
    formatValue (value) {
      return numbro(value || 0).format({ average: true, mantissa: 1, optionalMantissa: true })
    }
  },
  watch: {
    // The Total when the outputs add up to one, otherwise the first visible KPI.
    kpiNames: {
      immediate: true,
      handler (names) {
        if (names.indexOf(this.currentKpi) === -1) {
          this.currentKpi = names.indexOf('Total') !== -1 ? 'Total' : (names[0] || null)
        }
      }
    }
  }
}
